import { FC, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";

import { CollapseGroup } from "capitalrx-components-2.0/lib/general";
import { Grid, Stack, useResponsive, useResponsiveDisplayMode } from "capitalrx-components-2.0/lib/layout";
import { Paragraph } from "capitalrx-components-2.0/lib/typography";
import "./PlanSelect.scss";

import { EnrollmentPlanCSID } from "@/api/types";
import { PlanCard } from "@/premium/pages/PlanSelect/PlanCard";
import { Card, ContentContainer, ThemedTitle } from "@/premium/themedComponents";
import { ThemedH5 } from "@/premium/themedComponents/Headers";
import { EnrollmentContext } from "@/state";
import { ANALYTIC_EVENTS, logEvent } from "@/utils/analytics";

export const PlanSelect: FC = () => {
  const { t: translate, i18n } = useTranslation();
  const [state, _] = useContext(EnrollmentContext);
  const { isLoading } = state;
  const theme = state.experienceData.theme;
  const plans = state.experienceData.plan_csids;
  const faqs = state.experienceData.faqs;
  const [expandedFaqs, setExpandedFaqs] = useState<ReadonlyArray<string>>([]);
  const headingStyle = { fontFamily: (theme as { font?: string } | undefined)?.font || "Source Sans 3" };
  const bannerStyle = { borderBottom: `solid 8px ${theme?.primary_color || "#313e48"}` };
  const displayMode = useResponsiveDisplayMode();
  const containerClassName = displayMode === "desktop" ? "page-layout-grid" : "page-layout-grid page-layout-grid-mobile";

  const { headerColumns, enrollmentJourneyColumns, verticalHeader } = useResponsive(
    useMemo(
      () => ({
        headerColumns: { desktop: 6, tablet: 6, mobile: 12 },
        enrollmentJourneyColumns: { desktop: 4, tablet: 4, mobile: 12 },
        verticalHeader: { desktop: false, tablet: false, mobile: true },
      }),
      [],
    ),
  );

  const Header = useMemo(() => {
    if (verticalHeader) {
      return (
        <Grid className="plan-select-banner-grid" spacing="none">
          <Grid.Span columns={headerColumns}>
            <div className="plan-select-themed-banner" style={bannerStyle}>
              <div className="plan-select-image-container">
                <img className="plan-select-header-image-vertical" src={theme.home_image} alt={theme.home_image_alt_text} />
              </div>
            </div>
          </Grid.Span>
          <Grid.Span columns={headerColumns}>
            <div className="plan-select-gradient">
              {theme.header_primary_text && <ThemedTitle>{theme.header_primary_text}</ThemedTitle>}
              {theme.header_secondary_text && <Paragraph className="plan-select-banner-paragraph">{theme.header_secondary_text}</Paragraph>}
            </div>
          </Grid.Span>
        </Grid>
      );
    } else {
      return (
        <div className="plan-select-themed-banner" style={bannerStyle}>
          <Grid className="plan-select-banner-grid" spacing="none">
            <Grid.Span columns={headerColumns - 1}>
              <div className="plan-select-gradient">
                {theme.header_primary_text && <ThemedTitle>{theme.header_primary_text}</ThemedTitle>}
                {theme.header_secondary_text && <Paragraph className="plan-select-banner-paragraph">{theme.header_secondary_text}</Paragraph>}
              </div>
            </Grid.Span>
            <Grid.Span columns={headerColumns + 1}>
              <div className="plan-select-image-container">
                <img className="plan-select-header-image-horizontal" src={theme.home_image} alt={theme.home_image_alt_text} />
              </div>
            </Grid.Span>
          </Grid>
        </div>
      );
    }
  }, [verticalHeader, headerColumns, theme]);

  const Plans = useMemo(() => {
    if (isLoading) {
      return <div>{translate("PLAN_SELECT.planSelect.loadingPlans")}</div>;
    } else {
      return (
        <>
          <h2 className="plan-select-h2" style={headingStyle}>{translate("PLAN_SELECT.planSelect.selectPlanToLearnMore")}</h2>
          <Grid>
            {plans.map((plan: EnrollmentPlanCSID, idx: number) => {
              return (
                <Grid.Span columns={headerColumns} key={idx}>
                  <PlanCard id={plan.id}>{plan.display_name}</PlanCard>
                </Grid.Span>
              );
            })}
          </Grid>
        </>
      );
    }
  }, [plans, headerColumns, isLoading, translate, i18n.language]);

  const Faqs = useMemo(() => {
    if (isLoading) {
      return <div>{translate("PLAN_SELECT.planSelect.loadingFaqs")}</div>;
    } else {
      return (
        faqs.length > 0 && (
          <>
            <h2 className="plan-select-h2" style={headingStyle}>{translate("PLAN_SELECT.planSelect.frequentlyAskedQuestions")}</h2>
            <CollapseGroup alignCaret="left" spacing="none" expandedIds={expandedFaqs} onExpandedIdsChange={setExpandedFaqs}>
              {faqs.map((faq, idx) => {
                return (
                  <CollapseGroup.Collapse id={`faq-cg-${idx}`} key={idx} title={<div className="plan-select-question">{faq.question}</div>}>
                    <div className="plan-select-answer">
                      <Markdown>{faq.content}</Markdown>
                    </div>
                  </CollapseGroup.Collapse>
                );
              })}
            </CollapseGroup>
          </>
        )
      );
    }
  }, [faqs, expandedFaqs, isLoading, translate, i18n.language]);

  const EnrollmentJourney = useMemo(
    () => (
      <>
        <h2 className="plan-select-h2" style={headingStyle}>{translate("PLAN_SELECT.planSelect.yourEnrollmentJourney")}</h2>
        <Grid>
          <Grid.Span columns={enrollmentJourneyColumns}>
            <Card>
              <Stack>
                <img src={theme.step_01_image} alt={theme.step_01_image_alt_text} />
                <div className="plan-select-step-container">
                  <ThemedH5 $border={true}>{translate("PLAN_SELECT.planSelect.stepOne")}:</ThemedH5>
                </div>
                <Markdown className="plan-select-step-paragraph">{theme.step_01_image_caption || ""}</Markdown>
              </Stack>
            </Card>
          </Grid.Span>
          <Grid.Span columns={enrollmentJourneyColumns}>
            <Card fullHeight={true}>
              <Stack>
                <img src={theme.step_02_image} alt={theme.step_02_image_alt_text} />
                <div className="plan-select-step-container">
                  <ThemedH5>{translate("PLAN_SELECT.planSelect.stepTwo")}:</ThemedH5>
                </div>
                <Markdown className="plan-select-step-paragraph">{theme.step_02_image_caption || ""}</Markdown>
              </Stack>
            </Card>
          </Grid.Span>
          <Grid.Span columns={enrollmentJourneyColumns}>
            <Card fullHeight={true}>
              <Stack>
                <img src={theme.step_03_image} alt={theme.step_03_image_alt_text} />
                <div className="plan-select-step-container">
                  <ThemedH5>{translate("PLAN_SELECT.planSelect.stepThree")}:</ThemedH5>
                </div>
                <Markdown className="plan-select-step-paragraph">{theme.step_03_image_caption || ""}</Markdown>
              </Stack>
            </Card>
          </Grid.Span>
        </Grid>
      </>
    ),
    [enrollmentJourneyColumns, translate, i18n.language, theme],
  );

  useEffect(() => {
    logEvent(ANALYTIC_EVENTS.LANDING_PAGE);
  }, []);

  return (
    <ContentContainer>
      {Header}
      <Grid className={containerClassName}>
        {displayMode === "desktop" && <Grid.Span columns={1} />}
        <Grid.Span columns={displayMode === "desktop" ? 10 : 12}>
          <>
            {Plans}
            {Faqs}
            {theme.show_enrollment_journey && EnrollmentJourney}
          </>
        </Grid.Span>
        {displayMode === "desktop" && <Grid.Span columns={1} />}
      </Grid>
    </ContentContainer>
  );
};
